/**
 * Blood Bowl Calculator - State Management Hook
 *
 * Main reducer for calculator state with all player, action, and result management.
 */

import { useReducer, useCallback } from "react";
import type {
  CalculatorState,
  CalculatorAction,
  Player,
  ActionGroup,
  Action,
  SkillKey,
  LonerValue,
  ActionType,
  ActionResult,
  RerollPreference,
  CalculationResults,
  ComparisonChoice,
} from "../types";
import { getDefaultResult } from "../constants";

// ============================================================================
// Initial State
// ============================================================================

function createEmptyChoice(id: string, label: string): ComparisonChoice {
  return {
    id,
    label,
    actionGroups: [],
    results: null,
  };
}

function createInitialState(): CalculatorState {
  return {
    teamRerolls: 0,
    players: {},
    actionGroups: [],
    results: null,
    playerCounter: 0,
    groupCounter: 0,
    actionCounter: 0,
    // Comparison mode
    comparisonMode: false,
    choices: [
      createEmptyChoice("choice-a", "A"),
      createEmptyChoice("choice-b", "B"),
    ],
    activeChoiceIndex: 0,
  };
}

// ============================================================================
// Reducer
// ============================================================================

function calculatorReducer(state: CalculatorState, action: CalculatorAction): CalculatorState {
  switch (action.type) {
    case "SET_TEAM_REROLLS":
      return {
        ...state,
        teamRerolls: Math.max(0, Math.min(8, action.payload)),
        results: null,
      };

    case "ADD_PLAYER": {
      const newId = state.playerCounter + 1;
      const newPlayer: Player = {
        id: String(newId),
        number: newId,
        skills: {},
        loner: false,
        lonerValue: 4,
      };
      return {
        ...state,
        playerCounter: newId,
        players: {
          ...state.players,
          [newPlayer.id]: newPlayer,
        },
        results: null,
      };
    }

    case "REMOVE_PLAYER": {
      const { [action.payload]: removed, ...remainingPlayers } = state.players;
      // Update action groups to remove references to this player
      const updatedGroups = state.actionGroups.map((group) => ({
        ...group,
        playerId: group.playerId === action.payload ? null : group.playerId,
      }));
      return {
        ...state,
        players: remainingPlayers,
        actionGroups: updatedGroups,
        results: null,
      };
    }

    case "UPDATE_PLAYER_NUMBER": {
      const player = state.players[action.payload.id];
      if (!player) return state;
      return {
        ...state,
        players: {
          ...state.players,
          [action.payload.id]: {
            ...player,
            number: action.payload.number,
          },
        },
      };
    }

    case "TOGGLE_SKILL": {
      const player = state.players[action.payload.playerId];
      if (!player) return state;
      const currentValue = player.skills[action.payload.skill] || false;
      return {
        ...state,
        players: {
          ...state.players,
          [action.payload.playerId]: {
            ...player,
            skills: {
              ...player.skills,
              [action.payload.skill]: !currentValue,
            },
          },
        },
        results: null,
      };
    }

    case "SET_LONER": {
      const player = state.players[action.payload.playerId];
      if (!player) return state;
      return {
        ...state,
        players: {
          ...state.players,
          [action.payload.playerId]: {
            ...player,
            loner: action.payload.enabled,
          },
        },
        results: null,
      };
    }

    case "SET_LONER_VALUE": {
      const player = state.players[action.payload.playerId];
      if (!player) return state;
      return {
        ...state,
        players: {
          ...state.players,
          [action.payload.playerId]: {
            ...player,
            lonerValue: action.payload.value,
          },
        },
        results: null,
      };
    }

    case "ADD_ACTION_GROUP": {
      const newGroupId = state.groupCounter + 1;
      const newActionId = state.actionCounter + 1;
      const newAction: Action = {
        id: String(newActionId),
        groupId: String(newGroupId),
        type: "dodge",
        result: "2+",
        rerollPreference: "auto",
      };
      const newGroup: ActionGroup = {
        id: String(newGroupId),
        playerId: null,
        actions: [newAction],
      };
      return {
        ...state,
        groupCounter: newGroupId,
        actionCounter: newActionId,
        actionGroups: [...state.actionGroups, newGroup],
        results: null,
      };
    }

    case "REMOVE_ACTION_GROUP": {
      return {
        ...state,
        actionGroups: state.actionGroups.filter((g) => g.id !== action.payload),
        results: null,
      };
    }

    case "SET_GROUP_PLAYER": {
      return {
        ...state,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? { ...group, playerId: action.payload.playerId }
            : group
        ),
        results: null,
      };
    }

    case "ADD_ACTION": {
      const newActionId = state.actionCounter + 1;
      const newAction: Action = {
        id: String(newActionId),
        groupId: action.payload.groupId,
        type: "dodge",
        result: "2+",
        rerollPreference: "auto",
      };
      return {
        ...state,
        actionCounter: newActionId,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? { ...group, actions: [...group.actions, newAction] }
            : group
        ),
        results: null,
      };
    }

    case "REMOVE_ACTION": {
      return {
        ...state,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? { ...group, actions: group.actions.filter((a) => a.id !== action.payload.actionId) }
            : group
        ),
        results: null,
      };
    }

    case "UPDATE_ACTION_TYPE": {
      const defaultResult = getDefaultResult(action.payload.actionType);
      return {
        ...state,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? {
                ...group,
                actions: group.actions.map((a) =>
                  a.id === action.payload.actionId
                    ? { ...a, type: action.payload.actionType, result: defaultResult }
                    : a
                ),
              }
            : group
        ),
        results: null,
      };
    }

    case "UPDATE_ACTION_RESULT": {
      return {
        ...state,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? {
                ...group,
                actions: group.actions.map((a) =>
                  a.id === action.payload.actionId ? { ...a, result: action.payload.result } : a
                ),
              }
            : group
        ),
        results: null,
      };
    }

    case "UPDATE_ACTION_REROLL": {
      return {
        ...state,
        actionGroups: state.actionGroups.map((group) =>
          group.id === action.payload.groupId
            ? {
                ...group,
                actions: group.actions.map((a) =>
                  a.id === action.payload.actionId
                    ? { ...a, rerollPreference: action.payload.rerollPreference }
                    : a
                ),
              }
            : group
        ),
        results: null,
      };
    }

    case "SET_RESULTS":
      return {
        ...state,
        results: action.payload,
      };

    case "RESET":
      return createInitialState();

    // ========================================
    // Comparison Mode Actions
    // ========================================

    case "TOGGLE_COMPARISON_MODE": {
      if (state.comparisonMode) {
        // Exiting comparison mode - restore active choice's actions to main state
        const activeChoice = state.choices[state.activeChoiceIndex];
        return {
          ...state,
          comparisonMode: false,
          actionGroups: activeChoice.actionGroups,
          results: activeChoice.results,
        };
      } else {
        // Entering comparison mode - copy current actions to choice A
        const newChoices: [ComparisonChoice, ComparisonChoice] = [
          {
            ...state.choices[0],
            actionGroups: state.actionGroups,
            results: state.results,
          },
          createEmptyChoice("choice-b", "B"),
        ];
        return {
          ...state,
          comparisonMode: true,
          choices: newChoices,
          activeChoiceIndex: 0,
        };
      }
    }

    case "SET_ACTIVE_CHOICE": {
      if (!state.comparisonMode) return state;
      // Save current actionGroups to current choice before switching
      const updatedChoices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
      updatedChoices[state.activeChoiceIndex] = {
        ...updatedChoices[state.activeChoiceIndex],
        actionGroups: state.actionGroups,
        results: state.results,
      };
      // Load the new choice's action groups
      const newActiveChoice = updatedChoices[action.payload];
      return {
        ...state,
        choices: updatedChoices,
        activeChoiceIndex: action.payload,
        actionGroups: newActiveChoice.actionGroups,
        results: newActiveChoice.results,
      };
    }

    case "UPDATE_CHOICE_LABEL": {
      const updatedChoices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
      updatedChoices[action.payload.index] = {
        ...updatedChoices[action.payload.index],
        label: action.payload.label,
      };
      return {
        ...state,
        choices: updatedChoices,
      };
    }

    case "SET_CHOICE_RESULTS": {
      const updatedChoices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
      updatedChoices[action.payload.index] = {
        ...updatedChoices[action.payload.index],
        results: action.payload.results,
      };
      return {
        ...state,
        choices: updatedChoices,
      };
    }

    case "COPY_ACTIONS_TO_CHOICE": {
      const sourceChoice = state.choices[action.payload.fromIndex];
      const updatedChoices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
      // Deep copy the action groups with new IDs
      const copiedGroups = sourceChoice.actionGroups.map((group, gi) => ({
        ...group,
        id: `copied-${group.id}-${Date.now()}-${gi}`,
        actions: group.actions.map((act, ai) => ({
          ...act,
          id: `copied-${act.id}-${Date.now()}-${ai}`,
          groupId: `copied-${group.id}-${Date.now()}-${gi}`,
        })),
      }));
      updatedChoices[action.payload.toIndex] = {
        ...updatedChoices[action.payload.toIndex],
        actionGroups: copiedGroups,
        results: null,
      };
      // If copying to active choice, update main actionGroups too
      if (action.payload.toIndex === state.activeChoiceIndex) {
        return {
          ...state,
          choices: updatedChoices,
          actionGroups: copiedGroups,
          results: null,
        };
      }
      return {
        ...state,
        choices: updatedChoices,
      };
    }

    case "CLEAR_CHOICE": {
      const updatedChoices = [...state.choices] as [ComparisonChoice, ComparisonChoice];
      updatedChoices[action.payload] = createEmptyChoice(
        updatedChoices[action.payload].id,
        updatedChoices[action.payload].label
      );
      // If clearing active choice, clear main actionGroups too
      if (action.payload === state.activeChoiceIndex) {
        return {
          ...state,
          choices: updatedChoices,
          actionGroups: [],
          results: null,
        };
      }
      return {
        ...state,
        choices: updatedChoices,
      };
    }

    default:
      return state;
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useCalculatorState() {
  const [state, dispatch] = useReducer(calculatorReducer, null, createInitialState);

  // Convenience action creators
  const setTeamRerolls = useCallback((value: number) => {
    dispatch({ type: "SET_TEAM_REROLLS", payload: value });
  }, []);

  const addPlayer = useCallback(() => {
    dispatch({ type: "ADD_PLAYER" });
  }, []);

  const removePlayer = useCallback((id: string) => {
    dispatch({ type: "REMOVE_PLAYER", payload: id });
  }, []);

  const updatePlayerNumber = useCallback((id: string, number: number) => {
    dispatch({ type: "UPDATE_PLAYER_NUMBER", payload: { id, number } });
  }, []);

  const toggleSkill = useCallback((playerId: string, skill: SkillKey) => {
    dispatch({ type: "TOGGLE_SKILL", payload: { playerId, skill } });
  }, []);

  const setLoner = useCallback((playerId: string, enabled: boolean) => {
    dispatch({ type: "SET_LONER", payload: { playerId, enabled } });
  }, []);

  const setLonerValue = useCallback((playerId: string, value: LonerValue) => {
    dispatch({ type: "SET_LONER_VALUE", payload: { playerId, value } });
  }, []);

  const addActionGroup = useCallback(() => {
    dispatch({ type: "ADD_ACTION_GROUP" });
  }, []);

  const removeActionGroup = useCallback((groupId: string) => {
    dispatch({ type: "REMOVE_ACTION_GROUP", payload: groupId });
  }, []);

  const setGroupPlayer = useCallback((groupId: string, playerId: string | null) => {
    dispatch({ type: "SET_GROUP_PLAYER", payload: { groupId, playerId } });
  }, []);

  const addAction = useCallback((groupId: string) => {
    dispatch({ type: "ADD_ACTION", payload: { groupId } });
  }, []);

  const removeAction = useCallback((groupId: string, actionId: string) => {
    dispatch({ type: "REMOVE_ACTION", payload: { groupId, actionId } });
  }, []);

  const updateActionType = useCallback((groupId: string, actionId: string, actionType: ActionType) => {
    dispatch({ type: "UPDATE_ACTION_TYPE", payload: { groupId, actionId, actionType } });
  }, []);

  const updateActionResult = useCallback((groupId: string, actionId: string, result: ActionResult) => {
    dispatch({ type: "UPDATE_ACTION_RESULT", payload: { groupId, actionId, result } });
  }, []);

  const updateActionReroll = useCallback(
    (groupId: string, actionId: string, rerollPreference: RerollPreference) => {
      dispatch({ type: "UPDATE_ACTION_REROLL", payload: { groupId, actionId, rerollPreference } });
    },
    []
  );

  const setResults = useCallback((results: CalculationResults | null) => {
    dispatch({ type: "SET_RESULTS", payload: results });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  // Comparison mode actions
  const toggleComparisonMode = useCallback(() => {
    dispatch({ type: "TOGGLE_COMPARISON_MODE" });
  }, []);

  const setActiveChoice = useCallback((index: 0 | 1) => {
    dispatch({ type: "SET_ACTIVE_CHOICE", payload: index });
  }, []);

  const updateChoiceLabel = useCallback((index: 0 | 1, label: string) => {
    dispatch({ type: "UPDATE_CHOICE_LABEL", payload: { index, label } });
  }, []);

  const setChoiceResults = useCallback((index: 0 | 1, results: CalculationResults | null) => {
    dispatch({ type: "SET_CHOICE_RESULTS", payload: { index, results } });
  }, []);

  const copyActionsToChoice = useCallback((fromIndex: 0 | 1, toIndex: 0 | 1) => {
    dispatch({ type: "COPY_ACTIONS_TO_CHOICE", payload: { fromIndex, toIndex } });
  }, []);

  const clearChoice = useCallback((index: 0 | 1) => {
    dispatch({ type: "CLEAR_CHOICE", payload: index });
  }, []);

  return {
    state,
    dispatch,
    // Action creators
    setTeamRerolls,
    addPlayer,
    removePlayer,
    updatePlayerNumber,
    toggleSkill,
    setLoner,
    setLonerValue,
    addActionGroup,
    removeActionGroup,
    setGroupPlayer,
    addAction,
    removeAction,
    updateActionType,
    updateActionResult,
    updateActionReroll,
    setResults,
    reset,
    // Comparison mode
    toggleComparisonMode,
    setActiveChoice,
    updateChoiceLabel,
    setChoiceResults,
    copyActionsToChoice,
    clearChoice,
  };
}
